import type { ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * Shared visual shell for the unauthenticated auth screens (sign in, register,
 * check-email, phone verify). Brand-consistent with the landing: husk page,
 * shell card, acorn mark + wordmark, leaf accents. Colors come from the
 * nutshell palette CSS vars (apps/web/src/styles.css), which auto-adapt to
 * dark mode — so no `dark:` variants are needed here.
 */
export const authFieldClass =
  'w-full rounded-lg border border-ink/15 bg-husk/50 px-3.5 py-2.5 text-sm text-ink outline-none transition placeholder:text-ink-soft/60 focus:border-leaf focus:bg-shell focus:ring-2 focus:ring-leaf/25';

export const authButtonClass =
  'w-full rounded-lg bg-leaf px-4 py-2.5 text-center text-sm font-medium text-white shadow-sm transition hover:bg-leaf-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf/40 disabled:cursor-not-allowed disabled:opacity-50';

export function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center bg-husk px-4 py-10">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="mb-6 flex items-center justify-center gap-2"
          aria-label="Nutians home"
        >
          <img src="/nutians-mark.png" alt="" width={36} height={36} className="h-9 w-9" />
          <span className="text-xl font-semibold tracking-tight text-ink">nutians</span>
        </Link>
        <div className="rounded-2xl border border-ink/10 bg-shell p-8 shadow-sm">
          <div className="mb-6 flex flex-col gap-1.5">
            <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
            {subtitle ? <p className="text-sm leading-relaxed text-ink-soft">{subtitle}</p> : null}
          </div>
          {children}
        </div>
        {footer ? <div className="mt-6 text-center text-sm text-ink-soft">{footer}</div> : null}
      </div>
    </main>
  );
}
